import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext(null);

// FastAPI validation errors arrive as a list of objects; turn any detail into text
// so the pages never try to render an object.
const errorText = (err, fallback) => {
  const detail = err.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail) && detail.length) {
    return detail.map(d => (d.loc?.includes('email') ? 'Enter a valid email address' : d.msg)).join('. ');
  }
  return fallback;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};

const GOOGLE_CLIENT_ID = '802406774606-f0411hflvhb8vna9131asulrm80tgtug.apps.googleusercontent.com';

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(() => localStorage.getItem('auth_token'));
  // Pending verification state (signup flow)
  const [pendingVerification, setPendingVerification] = useState(null);

  const fetchMe = useCallback(async (t) => {
    try {
      const { data } = await axios.get(`${API}/unified-auth/me`, {
        headers: { Authorization: `Bearer ${t}` },
      });
      const u = data.user || data;
      if (!u.products || !u.products.length) u.products = ['agent9'];
      if (!u.default_product) u.default_product = u.products[0];
      const ob = u.onboarding || {};
      u.onboarding_complete = ob.complete === true || u.onboarding_complete === true;
      setUser(u);
    } catch {
      try {
        const { data } = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${t}` },
        });
        const ob = data.onboarding || {};
        data.onboarding_complete = ob.complete === true;
        data.products = data.products || ['agent9'];
        data.default_product = data.default_product || 'agent9';
        setUser(data);
      } catch {
        localStorage.removeItem('auth_token');
        localStorage.removeItem('vd_token');
        setToken(null);
        setUser(null);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (window.location.pathname === '/auth/callback') {
      setLoading(false);
      return;
    }
    if (token) {
      fetchMe(token);
    } else {
      setLoading(false);
    }
  }, [token, fetchMe]);

  const login = async (email, password) => {
    try {
      const { data } = await axios.post(`${API}/unified-auth/login`, { email, password });

      // Check if pending phone verification
      if (data.pending_verification) {
        setPendingVerification({
          phone: data.phone,
          masked_phone: data.masked_phone,
          products: data.products,
          user_id: data.user_id,
        });
        return { success: true, pending_verification: true, masked_phone: data.masked_phone };
      }

      const t = data.access_token;
      localStorage.setItem('auth_token', t);
      if (data.user?.products?.includes('voicedesk')) {
        localStorage.setItem('vd_token', t);
      }
      setToken(t);
      await fetchMe(t);
      return { success: true, user: data.user };
    } catch (err) {
      return { success: false, error: errorText(err, 'Login failed') };
    }
  };

  const signup = async (formData) => {
    try {
      const { data } = await axios.post(`${API}/unified-auth/signup`, formData);

      if (data.pending_verification) {
        setPendingVerification({
          phone: data.phone,
          masked_phone: data.masked_phone,
          products: data.products,
          user_id: data.user_id,
        });
        return { success: true, pending_verification: true, masked_phone: data.masked_phone };
      }

      const t = data.access_token;
      localStorage.setItem('auth_token', t);
      if (data.user?.products?.includes('voicedesk')) {
        localStorage.setItem('vd_token', t);
      }
      setToken(t);
      setUser(data.user);
      return { success: true, data };
    } catch (err) {
      return { success: false, error: errorText(err, 'Signup failed') };
    }
  };

  const verifyOTP = async (phone, code) => {
    try {
      const { data } = await axios.post(`${API}/unified-auth/verify-otp`, { phone, code });
      if (data.success && data.access_token) {
        const t = data.access_token;
        localStorage.setItem('auth_token', t);
        if (data.user?.products?.includes('voicedesk')) {
          localStorage.setItem('vd_token', t);
        }
        setToken(t);
        setUser(data.user);
        setPendingVerification(null);
        return { success: true, user: data.user };
      }
      return { success: false, error: 'Verification failed' };
    } catch (err) {
      return { success: false, error: errorText(err, 'Invalid code') };
    }
  };

  const resendOTP = async (phone) => {
    try {
      const { data } = await axios.post(`${API}/unified-auth/resend-otp`, { phone });
      return { success: true, message: data.message };
    } catch (err) {
      return { success: false, error: errorText(err, 'Could not resend code') };
    }
  };

  const setAuthFromGoogle = (data) => {
    localStorage.setItem('auth_token', data.access_token);
    setToken(data.access_token);
    setUser({
      id: data.agent_id,
      email: data.email,
      full_name: data.full_name,
      is_new_user: data.is_new_user,
      products: data.products || ['agent9'],
      default_product: data.default_product || 'agent9',
      onboarding_complete: data.is_new_user ? false : undefined,
    });
  };

  const switchProduct = (product) => {
    if (user && user.products?.includes(product)) {
      setUser({ ...user, default_product: product });
      const t = localStorage.getItem('auth_token');
      if (t) {
        axios.put(`${API}/unified-auth/default-product`, { product }, {
          headers: { Authorization: `Bearer ${t}` },
        }).catch(() => {});
      }
    }
  };

  const logout = () => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('vd_token');
    setToken(null);
    setUser(null);
    setPendingVerification(null);
  };

  const loginWithGoogle = () => {
    const redirectUri = window.location.origin + '/auth/callback';
    const scope = 'openid email profile';
    const url = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${GOOGLE_CLIENT_ID}&redirect_uri=${encodeURIComponent(redirectUri)}&response_type=code&scope=${encodeURIComponent(scope)}&access_type=offline&prompt=consent`;
    window.location.href = url;
  };

  const getPostLoginRoute = () => {
    if (!user) return '/login';
    const products = user.products || ['agent9'];
    if (products.length > 1) return '/select-product';
    const p = user.default_product || products[0];
    if (p === 'voicedesk') return '/voicedesk/dashboard';
    if (p === 'tele_sale') return '/telesales';
    return '/dashboard';
  };

  return (
    <AuthContext.Provider value={{
      user, token, loading,
      login, signup, logout,
      verifyOTP, resendOTP,
      pendingVerification, setPendingVerification,
      loginWithGoogle, setAuthFromGoogle, fetchMe,
      switchProduct, getPostLoginRoute,
      isAuthenticated: !!user,
      activeProduct: user?.default_product || 'agent9',
    }}>
      {children}
    </AuthContext.Provider>
  );
}
